import React, { useState } from "react";
import { observer } from "mobx-react";
import { toasterState } from "Components/common/toaster";
import { Utils } from "Utils/utils";
import { ContactTimelineStore } from "./ContactTimelineStore";

// SG-2249: log one timeline activity to a CRM the contact is in. The backend refuses anything already logged or live-synced.
const LOGGABLE_EVENTS = [
  "SENT",
  "OPENED",
  "CLICKED",
  "REPLIED",
  "CALL_MADE",
  "CALL_LOG",
  "LI_CONNECTION_REQ_SENT",
  "LI_CONNECTION_REQ_ACCEPTED",
  "LI_MESSAGE_SENT"
];

const CRM_NAMES = {
  HUBSPOT: "HubSpot",
  PIPEDRIVE: "Pipedrive",
  ZOHO: "Zoho",
  SALESFORCE: "Salesforce"
};

// result from the backend -> [status to show, toast message, toast type]
const RESULTS = {
  LOGGED: ["LOGGED", name => `Activity logged to ${name}`, "success"],
  QUEUED: [
    "QUEUED",
    name => `Activity will be logged to ${name} shortly`,
    "info"
  ],
  ALREADY_LOGGED: [
    "LOGGED",
    name => `Activity is already logged to ${name}`,
    "info"
  ],
  ALREADY_SYNCED: [
    "SYNCED",
    name => `Activity is already synced to ${name}`,
    "info"
  ],
  NOT_IN_CRM: ["", name => `Export this contact to ${name} first`, "failure"],
  NOT_CONNECTED: ["", name => `${name} is not connected`, "failure"],
  NOT_SUPPORTED: [
    "",
    name => `This activity can't be logged to ${name}`,
    "failure"
  ],
  NOT_FOUND: [
    "",
    name => `This activity can't be logged to ${name}`,
    "failure"
  ],
  FAILED: [
    "FAILED",
    name => `Failed to log activity to ${name}. Please try again`,
    "failure"
  ]
};

export const isCrmLoggable = (eventType = "", taskType = "") =>
  LOGGABLE_EVENTS.includes(eventType) ||
  (eventType === "TASK_COMPLETED" && taskType?.toLowerCase() === "customtask");

const CrmLogActions = observer(
  ({ activityId = "", eventType = "", taskType = "" }) => {
    const [loggingTo, setLoggingTo] = useState("");
    // status lists only connected CRMs the contact is in; null = not logged yet
    const statusMap = ContactTimelineStore.crmLogStatus?.[activityId] || {};
    const crms = Object.keys(statusMap);

    if (!activityId || !crms.length || !isCrmLoggable(eventType, taskType)) {
      return null;
    }

    const logTo = async (event, crmType) => {
      Utils.preventDefaultFn(event);
      if (loggingTo) return;
      setLoggingTo(crmType);
      const result = await ContactTimelineStore.logActivityToCrm(
        activityId,
        crmType
      );
      setLoggingTo("");
      const [status, message, type] = RESULTS[result] || RESULTS.FAILED;
      if (status === "QUEUED") {
        ContactTimelineStore.recheckCrmLogStatus([activityId]);
      }
      if (status) {
        ContactTimelineStore.setActivityCrmLogStatus(
          activityId,
          crmType,
          status
        );
      }
      toasterState.setToastMsg(message(CRM_NAMES[crmType]), type);
    };

    return (
      <div className="crmLogActions" onClick={Utils.preventDefaultFn}>
        {crms.map(crmType => {
          const name = CRM_NAMES[crmType];
          const status = loggingTo === crmType ? "LOGGING" : statusMap[crmType];
          if (status === "LOGGED" || status === "SYNCED") {
            return (
              <span className="crmLogged" key={crmType}>
                <i className="material-icons-outlined">check_circle</i>
                {status === "SYNCED"
                  ? `Synced to ${name}`
                  : `Logged to ${name}`}
              </span>
            );
          }
          if (status === "LOGGING" || status === "QUEUED") {
            return (
              <span className="crmLogging" key={crmType}>
                {`Logging to ${name}...`}
              </span>
            );
          }
          return (
            <button
              type="button"
              className="crmLogBtn"
              key={crmType}
              disabled={!!loggingTo}
              onClick={event => logTo(event, crmType)}
            >
              {status === "FAILED"
                ? `Retry logging to ${name}`
                : `Log to ${name}`}
            </button>
          );
        })}
      </div>
    );
  }
);

export default CrmLogActions;
